import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Target, 
  Calendar, 
  Users, 
  Award, 
  TrendingUp, 
  ClipboardList, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Eye,
  Send,
  Star,
  BarChart3,
  BookOpen,
  History,
  ChevronRight,
  User,
  Building2,
  Settings,
  Play,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { AIInsightsTab } from './AIInsightsTab';
import { MonthlyTrackingTab, BiasDetectionTab, GoalsTab, AIChatWidget } from './Phase2Tabs';

const PerformanceReviews = ({ user }) => {
  const [activeTab, setActiveTab] = useState('cycles');
  const [cycles, setCycles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Dialog states
  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [kraDialogOpen, setKraDialogOpen] = useState(false);
  const [selfAssessmentOpen, setSelfAssessmentOpen] = useState(false);
  const [managerReviewOpen, setManagerReviewOpen] = useState(false);
  const [hrApprovalOpen, setHrApprovalOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  // Form states
  const [cycleForm, setCycleForm] = useState({
    name: '',
    cycle_type: 'annual',
    start_date: '',
    end_date: '',
    self_assessment_deadline: '',
    manager_review_deadline: '',
    hr_approval_deadline: '',
    description: '',
    min_kras: 5,
    max_kras: 10
  });
  
  const [kras, setKras] = useState([]);
  const [employeeHistory, setEmployeeHistory] = useState([]);

  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loadingScheduler, setLoadingScheduler] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'hr_manager';
  const isManager = user?.role === 'manager' || isAdmin;

  useEffect(() => {
    fetchData();
    if (isAdmin) fetchSchedulerStatus();
  }, []);

  const fetchData = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const [cyclesRes, reviewsRes, employeesRes, statsRes] = await Promise.all([
        api.get('/performance/cycles').catch(() => ({ data: [] })),
        api.get('/performance/reviews').catch(() => ({ data: [] })),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/performance/dashboard/stats').catch(() => ({ data: null }))
      ]);
      setCycles(cyclesRes.data || []);
      setReviews(reviewsRes.data || []);
      setEmployees(employeesRes.data || []);
      setDashboardStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCycle = async () => {
    try {
      await api.post('/performance/cycles', cycleForm);
      toast.success('Appraisal cycle created successfully');
      setCycleDialogOpen(false);
      setCycleForm({
        name: '',
        cycle_type: 'annual',
        start_date: '',
        end_date: '',
        self_assessment_deadline: '',
        manager_review_deadline: '',
        hr_approval_deadline: '',
        description: '',
        min_kras: 5,
        max_kras: 10
      });
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cycle');
    }
  };

  const handleActivateCycle = async (cycleId) => {
    try {
      await api.post(`/performance/cycles/${cycleId}/activate`);
      toast.success('Cycle activated successfully');
      fetchData(false);
    } catch (error) {
      toast.error('Failed to activate cycle');
    }
  };

  const handleBulkCreateReviews = async (cycleId) => {
    try {
      const res = await api.post(`/performance/reviews/bulk-create?cycle_id=${cycleId}`);
      toast.success(`Created ${res.data.created} reviews, skipped ${res.data.skipped} existing`);
      fetchData(false);
    } catch (error) {
      toast.error('Failed to create reviews');
    }
  };

  const handleUpdateKras = async (reviewId) => {
    const totalWeightage = kras.reduce((sum, k) => sum + (parseFloat(k.weightage) || 0), 0);
    if (Math.abs(totalWeightage - 100) > 0.01) {
      toast.error('KRA weightages must add up to 100%');
      return;
    }
    
    try {
      await api.put(`/performance/reviews/${reviewId}/kras`, kras);
      toast.success('KRAs updated successfully');
      setKraDialogOpen(false);
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update KRAs');
    }
  };

  const handleSelfAssessmentSubmit = async (reviewId, formData) => {
    try {
      await api.post(`/performance/reviews/${reviewId}/self-assessment`, formData);
      toast.success('Self-assessment submitted successfully');
      setSelfAssessmentOpen(false);
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit self-assessment');
    }
  };

  const handleManagerReviewSubmit = async (reviewId, formData) => {
    try {
      await api.post(`/performance/reviews/${reviewId}/manager-review`, formData);
      toast.success('Manager review submitted successfully');
      setManagerReviewOpen(false);
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit manager review');
    }
  };

  const handleHrApproval = async (reviewId, approved, comments, finalRating) => {
    try {
      await api.post(`/performance/reviews/${reviewId}/hr-approval`, {
        approved,
        hr_comments: comments,
        final_rating: finalRating
      });
      toast.success(approved ? 'Review approved' : 'Review rejected');
      setHrApprovalOpen(false);
      fetchData(false);
    } catch (error) {
      toast.error('Failed to process approval');
    }
  };

  const fetchEmployeeHistory = async (employeeId) => {
    try {
      const res = await api.get(`/performance/employees/${employeeId}/kra-history`);
      setEmployeeHistory(res.data || []);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'not_started': { label: 'Not Started', className: 'bg-slate-100 text-slate-700' },
      'self_assessment': { label: 'Self Assessment', className: 'bg-amber-100 text-amber-700' },
      'manager_review': { label: 'Manager Review', className: 'bg-blue-100 text-blue-700' },
      'hr_approval': { label: 'HR Approval', className: 'bg-purple-100 text-purple-700' },
      'completed': { label: 'Completed', className: 'bg-emerald-100 text-emerald-700' },
      'rejected': { label: 'Rejected', className: 'bg-red-100 text-red-700' },
      'draft': { label: 'Draft', className: 'bg-slate-100 text-slate-700' },
      'active': { label: 'Active', className: 'bg-emerald-100 text-emerald-700' },
      'cancelled': { label: 'Cancelled', className: 'bg-red-100 text-red-700' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>{config.label}</span>;
  };

  const getRatingColor = (rating) => {
    if (rating >= 8) return 'text-emerald-600';
    if (rating >= 6) return 'text-blue-600';
    if (rating >= 4) return 'text-amber-600';
    return 'text-red-600';
  };

  // Dashboard Stats Component
  const DashboardStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card className="border-l-4 border-l-indigo-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Reviews</p>
              <p className="text-2xl font-bold">{dashboardStats?.total_reviews || 0}</p>
            </div>
            <ClipboardList className="h-8 w-8 text-indigo-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-l-4 border-l-emerald-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Completed</p>
              <p className="text-2xl font-bold">{dashboardStats?.completed_count || 0}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-l-4 border-l-amber-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Pending</p>
              <p className="text-2xl font-bold">{dashboardStats?.pending_count || 0}</p>
            </div>
            <Clock className="h-8 w-8 text-amber-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-l-4 border-l-purple-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Avg Rating</p>
              <p className="text-2xl font-bold">{dashboardStats?.average_rating || 0}/10</p>
            </div>
            <Star className="h-8 w-8 text-purple-500" />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Appraisal Cycles Tab
  const handleSendReminders = async (cycleId = null) => {
    setSendingReminders(true);
    try {
      const url = cycleId 
        ? `/performance/send-reminders?cycle_id=${cycleId}&days_before=7`
        : '/performance/send-reminders?days_before=7';
      const res = await api.post(url);
      toast.success(`Reminders sent! Self-assessment: ${res.data.self_assessment_reminders}, Manager: ${res.data.manager_review_reminders}`);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const cyclesTabContent = (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end gap-2">
          <Button 
            variant="outline" 
            onClick={() => handleSendReminders()}
            disabled={sendingReminders}
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
          >
            {sendingReminders ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600 mr-2"></div>
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send Deadline Reminders
          </Button>
          <Dialog open={cycleDialogOpen} onOpenChange={setCycleDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-cycle-btn">
                <Plus className="mr-2 h-4 w-4" /> Create Appraisal Cycle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>Create Appraisal Cycle</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="col-span-2">
                  <Label>Cycle Name</Label>
                  <Input
                    value={cycleForm.name}
                    onChange={(e) => setCycleForm(prev => ({...prev, name: e.target.value}))}
                    placeholder="e.g., Annual Review 2026"
                    data-testid="cycle-name-input"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label>Cycle Type</Label>
                  <Select value={cycleForm.cycle_type} onValueChange={(v) => setCycleForm(prev => ({...prev, cycle_type: v}))}>
                    <SelectTrigger data-testid="cycle-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="half_yearly">Half-Yearly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max KRAs</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={cycleForm.max_kras}
                    onChange={(e) => setCycleForm(prev => ({...prev, max_kras: parseInt(e.target.value)}))}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={cycleForm.start_date}
                    onChange={(e) => setCycleForm(prev => ({...prev, start_date: e.target.value}))}
                    data-testid="cycle-start-date"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={cycleForm.end_date}
                    onChange={(e) => setCycleForm(prev => ({...prev, end_date: e.target.value}))}
                    data-testid="cycle-end-date"
                  />
                </div>
                <div>
                  <Label>Self-Assessment Deadline</Label>
                  <Input
                    type="date"
                    value={cycleForm.self_assessment_deadline}
                    onChange={(e) => setCycleForm(prev => ({...prev, self_assessment_deadline: e.target.value}))}
                  />
                </div>
                <div>
                  <Label>Manager Review Deadline</Label>
                  <Input
                    type="date"
                    value={cycleForm.manager_review_deadline}
                    onChange={(e) => setCycleForm(prev => ({...prev, manager_review_deadline: e.target.value}))}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={cycleForm.description}
                    onChange={(e) => setCycleForm(prev => ({...prev, description: e.target.value}))}
                    placeholder="Optional description for this cycle"
                    autoComplete="off"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCycleDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateCycle} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-cycle-btn">
                  Create Cycle
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      
      <div className="grid gap-4">
        {cycles.map((cycle) => (
          <Card key={cycle.id} className="hover:shadow-md transition-shadow" data-testid={`cycle-card-${cycle.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-100 rounded-lg">
                    <Calendar className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{cycle.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {cycle.start_date} - {cycle.end_date}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 capitalize">{cycle.cycle_type?.replace('_', ' ')}</span>
                      {getStatusBadge(cycle.status)}
                      <span className="text-sm text-slate-500">KRAs: {cycle.min_kras}-{cycle.max_kras}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {cycle.status === 'draft' && isAdmin && (
                    <Button 
                      size="sm" 
                      onClick={() => handleActivateCycle(cycle.id)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      Activate
                    </Button>
                  )}
                  {cycle.status === 'active' && isAdmin && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleBulkCreateReviews(cycle.id)}
                    >
                      <Users className="mr-1 h-4 w-4" /> Create Reviews
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => {
                      setSelectedCycle(cycle);
                      setActiveTab('reviews');
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {cycles.length === 0 && (
          <Card className="p-8 text-center">
            <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No appraisal cycles created yet</p>
            {isAdmin && <p className="text-sm text-slate-400 mt-2">Click "Create Appraisal Cycle" to get started</p>}
          </Card>
        )}
      </div>
    </div>
  );

  const filteredReviews = selectedCycle 
    ? reviews.filter(r => r.cycle_id === selectedCycle.id)
    : reviews;

  const reviewsTabContent = (
      <div className="space-y-4">
        {selectedCycle && (
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
              Filtered by: {selectedCycle.name}
            </span>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => setSelectedCycle(null)}
            >
              Clear filter
            </Button>
          </div>
        )}
        
        <div className="grid gap-4">
          {filteredReviews.map((review) => (
            <Card key={review.id} className="hover:shadow-md transition-shadow" data-testid={`review-card-${review.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                      {review.employee_name?.substring(0, 2).toUpperCase() || 'NA'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{review.employee_name || 'Unknown Employee'}</h3>
                      <p className="text-sm text-slate-500">
                        {review.employee_code} | {review.department} | {review.designation}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        {getStatusBadge(review.status)}
                        {review.final_rating && (
                          <span className={`font-semibold ${getRatingColor(review.final_rating)}`}>
                            Rating: {review.final_rating}/10
                          </span>
                        )}
                        <span className="text-sm text-slate-400">{review.cycle_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {review.status === 'not_started' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedReview(review);
                          setKras(review.kras?.length > 0 ? review.kras : [{ title: '', description: '', weightage: 100, target: '', measurement_criteria: '' }]);
                          setKraDialogOpen(true);
                        }}
                      >
                        <Target className="mr-1 h-4 w-4" /> Set KRAs
                      </Button>
                    )}
                    {review.status === 'self_assessment' && (
                      <Button 
                        size="sm" 
                        className="bg-amber-500 hover:bg-amber-600"
                        onClick={() => {
                          setSelectedReview(review);
                          setSelfAssessmentOpen(true);
                        }}
                      >
                        <Edit className="mr-1 h-4 w-4" /> Self Assess
                      </Button>
                    )}
                    {review.status === 'manager_review' && isManager && (
                      <Button 
                        size="sm" 
                        className="bg-blue-500 hover:bg-blue-600"
                        onClick={() => {
                          setSelectedReview(review);
                          setManagerReviewOpen(true);
                        }}
                      >
                        <ClipboardList className="mr-1 h-4 w-4" /> Manager Review
                      </Button>
                    )}
                    {review.status === 'hr_approval' && isAdmin && (
                      <Button 
                        size="sm" 
                        className="bg-purple-500 hover:bg-purple-600"
                        onClick={() => {
                          setSelectedReview(review);
                          setHrApprovalOpen(true);
                        }}
                      >
                        <CheckCircle className="mr-1 h-4 w-4" /> HR Approve
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => fetchEmployeeHistory(review.employee_id)}
                    >
                      <History className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* KRA Progress */}
                {review.kras && review.kras.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-slate-600 mb-2">KRAs ({review.kras.length})</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                      {review.kras.slice(0, 5).map((kra, idx) => (
                        <div key={kra.id} className="bg-slate-50 rounded p-2 text-xs">
                          <p className="font-medium truncate">{kra.title}</p>
                          <p className="text-slate-500">Weight: {kra.weightage}%</p>
                          {kra.manager_rating && (
                            <p className={getRatingColor(kra.manager_rating)}>Rating: {kra.manager_rating}/10</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          
          {filteredReviews.length === 0 && (
            <Card className="p-8 text-center">
              <ClipboardList className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No performance reviews found</p>
            </Card>
          )}
        </div>
      </div>
  );

  // KRA Setup Dialog
  const KRADialog = () => {
    const addKra = () => {
      setKras([...kras, { title: '', description: '', weightage: 0, target: '', measurement_criteria: '' }]);
    };

    const removeKra = (index) => {
      setKras(kras.filter((_, i) => i !== index));
    };

    const updateKra = (index, field, value) => {
      const newKras = [...kras];
      newKras[index][field] = value;
      setKras(newKras);
    };

    const totalWeightage = kras.reduce((sum, k) => sum + (parseFloat(k.weightage) || 0), 0);

    return (
      <Dialog open={kraDialogOpen} onOpenChange={setKraDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Set KRAs / Goals for {selectedReview?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-500">
                Total Weightage: <span className={totalWeightage === 100 ? 'text-emerald-600 font-bold' : 'text-red-600 font-bold'}>{totalWeightage}%</span>
                {totalWeightage !== 100 && <span className="text-red-500 ml-2">(Must be 100%)</span>}
              </p>
              <Button size="sm" variant="outline" onClick={addKra}>
                <Plus className="mr-1 h-4 w-4" /> Add KRA
              </Button>
            </div>
            
            <div className="space-y-4">
              {kras.map((kra, index) => (
                <Card key={index} className="p-4">
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-4">
                      <Label className="text-xs">KRA Title</Label>
                      <Input
                        value={kra.title}
                        onChange={(e) => updateKra(index, 'title', e.target.value)}
                        placeholder="e.g., Project Delivery"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Weightage (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={kra.weightage}
                        onChange={(e) => updateKra(index, 'weightage', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Target</Label>
                      <Input
                        value={kra.target || ''}
                        onChange={(e) => updateKra(index, 'target', e.target.value)}
                        placeholder="e.g., 95% on-time delivery"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Measurement</Label>
                      <Input
                        value={kra.measurement_criteria || ''}
                        onChange={(e) => updateKra(index, 'measurement_criteria', e.target.value)}
                        placeholder="How measured"
                      />
                    </div>
                    <div className="col-span-1 flex items-end">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-500"
                        onClick={() => removeKra(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="col-span-12">
                      <Label className="text-xs">Description</Label>
                      <Textarea
                        value={kra.description || ''}
                        onChange={(e) => updateKra(index, 'description', e.target.value)}
                        placeholder="Detailed description of this KRA"
                        rows={2}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKraDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => handleUpdateKras(selectedReview?.id)}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={totalWeightage !== 100}
            >
              Save KRAs & Start Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Self Assessment Dialog
  const SelfAssessmentDialog = () => {
    const [formData, setFormData] = useState({
      kra_ratings: [],
      goals_achieved: '',
      areas_of_improvement: '',
      strengths: '',
      training_needs: '',
      overall_comments: ''
    });

    useEffect(() => {
      if (selectedReview?.kras) {
        setFormData(prev => ({
          ...prev,
          kra_ratings: selectedReview.kras.map(k => ({
            kra_id: k.id,
            self_rating: k.self_rating || 5,
            self_comments: k.self_comments || '',
            achievement: k.achievement || ''
          }))
        }));
      }
    }, [selectedReview]);

    const updateKraRating = (kraId, field, value) => {
      setFormData(prev => ({
        ...prev,
        kra_ratings: prev.kra_ratings.map(kr => 
          kr.kra_id === kraId ? { ...kr, [field]: value } : kr
        )
      }));
    };

    return (
      <Dialog open={selfAssessmentOpen} onOpenChange={setSelfAssessmentOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Self Assessment - {selectedReview?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6">
            {/* KRA Ratings */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Target className="h-5 w-5 text-indigo-600" /> Rate Your KRAs
              </h4>
              <div className="space-y-4">
                {selectedReview?.kras?.map((kra) => {
                  const kraRating = formData.kra_ratings.find(kr => kr.kra_id === kra.id) || {};
                  return (
                    <Card key={kra.id} className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h5 className="font-medium">{kra.title}</h5>
                          <p className="text-sm text-slate-500">Weightage: {kra.weightage}% | Target: {kra.target || 'N/A'}</p>
                        </div>
                        <div className="text-right">
                          <Label className="text-xs">Self Rating</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={1}
                              max={10}
                              className="w-20"
                              value={kraRating.self_rating || 5}
                              onChange={(e) => updateKraRating(kra.id, 'self_rating', parseInt(e.target.value))}
                            />
                            <span className="text-sm text-slate-500">/10</span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Achievement</Label>
                          <Textarea
                            value={kraRating.achievement || ''}
                            onChange={(e) => updateKraRating(kra.id, 'achievement', e.target.value)}
                            placeholder="What you achieved against this KRA"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Comments</Label>
                          <Textarea
                            value={kraRating.self_comments || ''}
                            onChange={(e) => updateKraRating(kra.id, 'self_comments', e.target.value)}
                            placeholder="Additional comments"
                            rows={2}
                          />
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
            
            {/* Overall Assessment */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Goals Achieved</Label>
                <Textarea
                  value={formData.goals_achieved}
                  onChange={(e) => setFormData({...formData, goals_achieved: e.target.value})}
                  placeholder="Summary of goals you achieved this period"
                  rows={3}
                />
              </div>
              <div>
                <Label>Your Strengths</Label>
                <Textarea
                  value={formData.strengths}
                  onChange={(e) => setFormData({...formData, strengths: e.target.value})}
                  placeholder="Key strengths you demonstrated"
                  rows={3}
                />
              </div>
              <div>
                <Label>Areas of Improvement</Label>
                <Textarea
                  value={formData.areas_of_improvement}
                  onChange={(e) => setFormData({...formData, areas_of_improvement: e.target.value})}
                  placeholder="Areas where you can improve"
                  rows={3}
                />
              </div>
              <div>
                <Label>Training Needs</Label>
                <Textarea
                  value={formData.training_needs}
                  onChange={(e) => setFormData({...formData, training_needs: e.target.value})}
                  placeholder="Training or development you need"
                  rows={3}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelfAssessmentOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => handleSelfAssessmentSubmit(selectedReview?.id, formData)}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Send className="mr-2 h-4 w-4" /> Submit Self Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Manager Review Dialog
  const ManagerReviewDialog = () => {
    const [formData, setFormData] = useState({
      kra_ratings: [],
      overall_rating: 5,
      goals_achieved_assessment: '',
      areas_of_improvement: '',
      strengths_observed: '',
      training_recommended: false,
      training_recommendations: '',
      promotion_recommendation: false,
      promotion_comments: '',
      overall_feedback: ''
    });

    useEffect(() => {
      if (selectedReview?.kras) {
        setFormData(prev => ({
          ...prev,
          kra_ratings: selectedReview.kras.map(k => ({
            kra_id: k.id,
            manager_rating: k.manager_rating || 5,
            manager_comments: k.manager_comments || ''
          }))
        }));
      }
    }, [selectedReview]);

    const updateKraRating = (kraId, field, value) => {
      setFormData(prev => ({
        ...prev,
        kra_ratings: prev.kra_ratings.map(kr => 
          kr.kra_id === kraId ? { ...kr, [field]: value } : kr
        )
      }));
    };

    return (
      <Dialog open={managerReviewOpen} onOpenChange={setManagerReviewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manager Review - {selectedReview?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6">
            {/* Employee Self Assessment Summary */}
            {selectedReview?.self_assessment && (
              <Card className="p-4 bg-amber-50 border-amber-200">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <User className="h-4 w-4" /> Employee Self Assessment
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600"><strong>Goals Achieved:</strong> {selectedReview.self_assessment.goals_achieved || 'Not provided'}</p>
                    <p className="text-slate-600"><strong>Strengths:</strong> {selectedReview.self_assessment.strengths || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-slate-600"><strong>Areas to Improve:</strong> {selectedReview.self_assessment.areas_of_improvement || 'Not provided'}</p>
                    <p className="text-slate-600"><strong>Training Needs:</strong> {selectedReview.self_assessment.training_needs || 'Not provided'}</p>
                  </div>
                </div>
              </Card>
            )}

            {/* KRA Ratings */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" /> Rate Employee KRAs
              </h4>
              <div className="space-y-4">
                {selectedReview?.kras?.map((kra) => {
                  const kraRating = formData.kra_ratings.find(kr => kr.kra_id === kra.id) || {};
                  return (
                    <Card key={kra.id} className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h5 className="font-medium">{kra.title}</h5>
                          <p className="text-sm text-slate-500">Weight: {kra.weightage}% | Target: {kra.target || 'N/A'}</p>
                          {kra.self_rating && (
                            <p className="text-sm text-amber-600">
                              Self Rating: {kra.self_rating}/10 | Achievement: {kra.achievement || 'N/A'}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <Label className="text-xs">Manager Rating</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={1}
                              max={10}
                              className="w-20"
                              value={kraRating.manager_rating || 5}
                              onChange={(e) => updateKraRating(kra.id, 'manager_rating', parseInt(e.target.value))}
                            />
                            <span className="text-sm text-slate-500">/10</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Manager Comments</Label>
                        <Textarea
                          value={kraRating.manager_comments || ''}
                          onChange={(e) => updateKraRating(kra.id, 'manager_comments', e.target.value)}
                          placeholder="Your feedback on this KRA"
                          rows={2}
                        />
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
            
            {/* Overall Assessment */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Overall Rating (1-10)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    className="w-24"
                    value={formData.overall_rating}
                    onChange={(e) => setFormData({...formData, overall_rating: parseInt(e.target.value)})}
                  />
                  <div className="flex-1">
                    <Progress value={formData.overall_rating * 10} className="h-3" />
                  </div>
                </div>
              </div>
              <div>
                <Label>Goals Achievement Assessment</Label>
                <Textarea
                  value={formData.goals_achieved_assessment}
                  onChange={(e) => setFormData({...formData, goals_achieved_assessment: e.target.value})}
                  rows={3}
                />
              </div>
              <div>
                <Label>Strengths Observed</Label>
                <Textarea
                  value={formData.strengths_observed}
                  onChange={(e) => setFormData({...formData, strengths_observed: e.target.value})}
                  rows={3}
                />
              </div>
              <div>
                <Label>Areas of Improvement</Label>
                <Textarea
                  value={formData.areas_of_improvement}
                  onChange={(e) => setFormData({...formData, areas_of_improvement: e.target.value})}
                  rows={3}
                />
              </div>
              <div>
                <Label>Overall Feedback</Label>
                <Textarea
                  value={formData.overall_feedback}
                  onChange={(e) => setFormData({...formData, overall_feedback: e.target.value})}
                  rows={3}
                />
              </div>
            </div>

            {/* Training & Promotion */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    checked={formData.training_recommended}
                    onCheckedChange={(c) => setFormData({...formData, training_recommended: c})}
                  />
                  <Label className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" /> Training Recommended
                  </Label>
                </div>
                {formData.training_recommended && (
                  <Textarea
                    value={formData.training_recommendations}
                    onChange={(e) => setFormData({...formData, training_recommendations: e.target.value})}
                    placeholder="Recommended training programs"
                    rows={2}
                  />
                )}
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    checked={formData.promotion_recommendation}
                    onCheckedChange={(c) => setFormData({...formData, promotion_recommendation: c})}
                  />
                  <Label className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" /> Recommend for Promotion
                  </Label>
                </div>
                {formData.promotion_recommendation && (
                  <Textarea
                    value={formData.promotion_comments}
                    onChange={(e) => setFormData({...formData, promotion_comments: e.target.value})}
                    placeholder="Promotion justification"
                    rows={2}
                  />
                )}
              </Card>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManagerReviewOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => handleManagerReviewSubmit(selectedReview?.id, formData)}
              className="bg-blue-500 hover:bg-blue-600"
            >
              <Send className="mr-2 h-4 w-4" /> Submit Manager Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // HR Approval Dialog
  const HRApprovalDialog = () => {
    const [approved, setApproved] = useState(true);
    const [comments, setComments] = useState('');
    const [finalRating, setFinalRating] = useState(selectedReview?.overall_rating || 5);

    useEffect(() => {
      if (selectedReview?.overall_rating) {
        setFinalRating(selectedReview.overall_rating);
      }
    }, [selectedReview]);

    return (
      <Dialog open={hrApprovalOpen} onOpenChange={setHrApprovalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>HR Approval - {selectedReview?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Card className="p-4 bg-slate-50">
              <h4 className="font-semibold mb-2">Review Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p><strong>Employee:</strong> {selectedReview?.employee_name}</p>
                <p><strong>Department:</strong> {selectedReview?.department}</p>
                <p><strong>Manager Rating:</strong> {selectedReview?.overall_rating}/10</p>
                <p><strong>Cycle:</strong> {selectedReview?.cycle_name}</p>
              </div>
            </Card>

            <div>
              <Label>Final Rating (Can adjust if needed)</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  min={1}
                  max={10}
                  className="w-24"
                  value={finalRating}
                  onChange={(e) => setFinalRating(parseInt(e.target.value))}
                />
                <Progress value={finalRating * 10} className="flex-1 h-3" />
              </div>
            </div>

            <div>
              <Label>HR Comments</Label>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Any comments or observations"
                rows={3}
              />
            </div>

            <div className="flex gap-4">
              <Button 
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                onClick={() => handleHrApproval(selectedReview?.id, true, comments, finalRating)}
              >
                <CheckCircle className="mr-2 h-4 w-4" /> Approve
              </Button>
              <Button 
                variant="destructive"
                className="flex-1"
                onClick={() => handleHrApproval(selectedReview?.id, false, comments, finalRating)}
              >
                <AlertCircle className="mr-2 h-4 w-4" /> Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // History Dialog
  const HistoryDialog = () => (
    <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" /> Previous Years KRA History
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {employeeHistory.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No historical reviews found</p>
          ) : (
            employeeHistory.map((history, idx) => (
              <Card key={idx} className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold">{history.cycle_name}</h4>
                    <p className="text-sm text-slate-500">{history.period}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 capitalize">{history.cycle_type?.replace('_', ' ')}</span>
                    {history.final_rating && (
                      <span className={`font-bold text-lg ${getRatingColor(history.final_rating)}`}>
                        {history.final_rating}/10
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  {history.kras?.map((kra, kraIdx) => (
                    <div key={kraIdx} className="flex justify-between items-center bg-slate-50 p-2 rounded text-sm">
                      <div>
                        <span className="font-medium">{kra.title}</span>
                        <span className="text-slate-500 ml-2">({kra.weightage}%)</span>
                      </div>
                      <div className="flex gap-4">
                        {kra.self_rating && <span className="text-amber-600">Self: {kra.self_rating}</span>}
                        {kra.manager_rating && <span className="text-blue-600">Manager: {kra.manager_rating}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  // Training Needs Tab
  const TrainingNeedsTab = () => {
    const [trainingNeeds, setTrainingNeeds] = useState([]);
    const [loadingTraining, setLoadingTraining] = useState(false);

    useEffect(() => {
      const fetchTrainingNeeds = async () => {
        setLoadingTraining(true);
        try {
          const res = await api.get('/performance/training-needs');
          setTrainingNeeds(res.data || []);
        } catch (error) {
          console.error('Error fetching training needs:', error);
        } finally {
          setLoadingTraining(false);
        }
      };
      fetchTrainingNeeds();
    }, []);

    return (
      <div className="space-y-4">
        <Card className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-purple-600" />
            <div>
              <h3 className="font-semibold">Training Recommendations</h3>
              <p className="text-sm text-slate-600">Employees recommended for training based on performance reviews</p>
            </div>
          </div>
        </Card>

        {loadingTraining ? (
          <div className="text-center py-8">Loading...</div>
        ) : trainingNeeds.length === 0 ? (
          <Card className="p-8 text-center">
            <BookOpen className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No training recommendations found</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {trainingNeeds.map((need, idx) => (
              <Card key={idx} className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">{need.employee_name}</h4>
                    <p className="text-sm text-slate-500">{need.department}</p>
                    <div className="mt-2 space-y-1">
                      {need.training_recommendations && (
                        <p className="text-sm"><strong>Manager Recommendation:</strong> {need.training_recommendations}</p>
                      )}
                      {need.self_training_needs && (
                        <p className="text-sm text-slate-600"><strong>Self Identified:</strong> {need.self_training_needs}</p>
                      )}
                    </div>
                  </div>
                  {need.final_rating && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRatingColor(need.final_rating)} bg-slate-100`}>
                      Rating: {need.final_rating}/10
                    </span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  const fetchSchedulerStatus = async () => {
    setLoadingScheduler(true);
    try {
      const res = await api.get('/scheduler/status');
      setSchedulerStatus(res.data);
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    } finally {
      setLoadingScheduler(false);
    }
  };

  const handleTriggerReminders = async () => {
    setTriggering(true);
    try {
      await api.post('/scheduler/trigger-reminders');
      toast.success('Reminder job triggered! Check logs for results.');
      setTimeout(fetchSchedulerStatus, 2000);
    } catch (error) {
      toast.error('Failed to trigger reminders');
    } finally {
      setTriggering(false);
    }
  };

  const schedulerTabContent = (
      <div className="space-y-4">
        <Card className="p-4 bg-gradient-to-r from-slate-50 to-indigo-50 border-indigo-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="h-8 w-8 text-indigo-600" />
              <div>
                <h3 className="font-semibold">Automated Deadline Reminders</h3>
                <p className="text-sm text-slate-600">Daily scheduled job sends reminders for approaching deadlines</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchSchedulerStatus}
                disabled={loadingScheduler}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingScheduler ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={handleTriggerReminders}
                disabled={triggering}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {triggering ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Run Now
              </Button>
            </div>
          </div>
        </Card>

        {loadingScheduler && !schedulerStatus ? (
          <div className="text-center py-8">Loading scheduler status...</div>
        ) : schedulerStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-indigo-600" />
                Scheduler Status
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    schedulerStatus.running 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {schedulerStatus.running ? 'Running' : 'Stopped'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Schedule Time</span>
                  <span className="font-medium">{schedulerStatus.config?.reminder_time} {schedulerStatus.config?.timezone}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Reminder Window</span>
                  <span className="font-medium">{schedulerStatus.config?.reminder_days_before} days before deadline</span>
                </div>
              </div>

              {schedulerStatus.jobs?.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-slate-600 mb-2">Scheduled Jobs</p>
                  {schedulerStatus.jobs.map((job, idx) => (
                    <div key={idx} className="bg-slate-50 rounded p-2 text-sm">
                      <p className="font-medium">{job.name}</p>
                      <p className="text-slate-500 text-xs">
                        Next run: {job.next_run ? new Date(job.next_run).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <History className="h-5 w-5 text-indigo-600" />
                Recent Job Runs
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {schedulerStatus.recent_logs?.length > 0 ? (
                  schedulerStatus.recent_logs.map((log, idx) => (
                    <div key={idx} className={`p-2 rounded text-sm ${
                      log.status === 'completed' ? 'bg-emerald-50' : 'bg-red-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          log.status === 'completed' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {log.status}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(log.run_time).toLocaleString()}
                        </span>
                      </div>
                      {log.results && (
                        <p className="text-xs text-slate-600 mt-1">
                          Self-assessment: {log.results.self_assessment}, Manager: {log.results.manager_review}, Errors: {log.results.errors}
                        </p>
                      )}
                      {log.error && (
                        <p className="text-xs text-red-600 mt-1">{log.error}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-4">No recent job runs</p>
                )}
              </div>
            </Card>
          </div>
        ) : (
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Unable to load scheduler status</p>
          </Card>
        )}

        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>The scheduler runs automatically every day at 9:00 AM IST</li>
                <li>Sends self-assessment reminders to employees 3 days before deadline</li>
                <li>Sends manager review reminders to managers 3 days before deadline</li>
                <li>Only sends reminders for active appraisal cycles</li>
                <li>Click "Run Now" to manually trigger reminders</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
  );

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="performance-reviews-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Performance Reviews</h1>
            <p className="text-slate-500 mt-1">Manage appraisal cycles, KRAs, and employee evaluations</p>
          </div>
          <div className="flex items-center gap-2">
            <Award className="h-8 w-8 text-indigo-600" />
          </div>
        </div>

        {/* Dashboard Stats */}
        {isAdmin && <DashboardStats />}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="cycles" className="data-[state=active]:bg-white">
              <Calendar className="mr-2 h-4 w-4" /> Appraisal Cycles
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-white">
              <ClipboardList className="mr-2 h-4 w-4" /> Reviews
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="training" className="data-[state=active]:bg-white">
                <BookOpen className="mr-2 h-4 w-4" /> Training Needs
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="scheduler" className="data-[state=active]:bg-white">
                <Settings className="mr-2 h-4 w-4" /> Scheduler
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="ai-insights" className="data-[state=active]:bg-white">
                <Sparkles className="mr-2 h-4 w-4" /> AI Insights
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="monthly-tracking" className="data-[state=active]:bg-white" data-testid="monthly-tracking-tab">
                <Calendar className="mr-2 h-4 w-4" /> Monthly Tracking
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="bias-detection" className="data-[state=active]:bg-white" data-testid="bias-detection-tab">
                <AlertCircle className="mr-2 h-4 w-4" /> Bias Detection
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="goals" className="data-[state=active]:bg-white" data-testid="goals-tab">
                <Target className="mr-2 h-4 w-4" /> Goals
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="cycles" className="mt-4">
            {cyclesTabContent}
          </TabsContent>

          <TabsContent value="reviews" className="mt-4">
            {reviewsTabContent}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="training" className="mt-4">
              <TrainingNeedsTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="scheduler" className="mt-4">
              {schedulerTabContent}
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="ai-insights" className="mt-4">
              <AIInsightsTab employees={employees} reviews={reviews} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="monthly-tracking" className="mt-4">
              <MonthlyTrackingTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="bias-detection" className="mt-4">
              <BiasDetectionTab cycles={cycles} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="goals" className="mt-4">
              <GoalsTab employees={employees} />
            </TabsContent>
          )}
        </Tabs>

        {/* Dialogs */}
        <KRADialog />
        <SelfAssessmentDialog />
        <ManagerReviewDialog />
        <HRApprovalDialog />
        <HistoryDialog />
      </div>
      {isAdmin && <AIChatWidget />}
    </Layout>
  );
};

export default PerformanceReviews;
